'use client';

import { c as _c } from "react/compiler-runtime";
import { ClerkProvider } from '@clerk/nextjs';
import { ClientThemeProvider } from '@wrksz/themes/client';
import { observer } from 'mobx-react-lite';
import { type ReactNode } from 'react';
import { isUpdaterRecoveryInProgress, requestManualUpdateCheck, useInitApp } from '@/app/editor/hooks/useInitApp';
import { globalStore } from '@/app/globalStore';
import { MINIMUM_SDK_VERSION } from '@/app/utils/versionUtils';
import { GlobalBuildErrorOverlay } from '@/components/ErrorOverlay';
import { GlobalLoadingOverlay, LoadingOverlay } from '@/components/LoadingOvelay';
import { TitleBar } from '@/components/title-bar';
import { Toaster } from '@/components/ui/toast';
import { ENGINE_VERSION } from '@/const';
import { AuthProvider } from '@/context/AuthContext';
import { SdkUpdateWaitingDialog } from '@/editor/components/dialogs/SdkUpdateWaitingDialog';
import { VersionCheckDialog } from '@/editor/components/dialogs/VersionCheckDialog';
import { useDeepLinkAuth } from '@/hooks/useDeepLinkAuth';
import { isClerkAuth } from '@/lib/auth/authConfig';
import { IS_DEV } from '@/lib/utils';
type ClientLayoutProps = {
  children: ReactNode;
  /** Auth routes skip SDK init and the app chrome so sign-in can render immediately. */
  variant?: 'app' | 'auth';
};

/** Wrapper that conditionally includes ClerkProvider */
function AuthWrapper(t0) {
  const $ = _c(4);
  const {
    children
  } = t0;
  useDeepLinkAuth();
  if (isClerkAuth) {
    let t1;
    if ($[0] !== children) {
      t1 = <ClerkProvider><AuthProvider>{children}</AuthProvider></ClerkProvider>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children) {
    t1 = <AuthProvider>{children}</AuthProvider>;
    $[2] = children;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
const ClientLayoutProviders = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ClientThemeProvider attribute="class" disableTransitionOnChange={true} enableColorScheme={true} forcedTheme="dark" storage="none" target="body"><div id="genesys-client-shell" className="h-full min-h-full"><AuthWrapper>{children}</AuthWrapper></div></ClientThemeProvider>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
ClientLayoutProviders.displayName = 'ClientLayoutProviders';
const ClientAppShell = observer(({
  children
}: {
  children: ReactNode;
}) => {
  const {
    isInitialized,
    isVersionValid,
    currentSdkVersion,
    currentSdkEngineVersion,
    sdkCompatibilityMode,
    sdkCompatibilityReason
  } = useInitApp();
  const updaterRecoveryActive = isUpdaterRecoveryInProgress(globalStore.updaterPhase);
  const showWaitingDialog = !IS_DEV && isInitialized && !!currentSdkVersion && (sdkCompatibilityMode === 'updateRequiredWaiting' || sdkCompatibilityMode === 'manualUpdateRequired' && updaterRecoveryActive);
  const showManualDialog = !IS_DEV && isInitialized && sdkCompatibilityMode === 'manualUpdateRequired' && !!currentSdkVersion && !updaterRecoveryActive;
  if (!isInitialized) {
    return <LoadingOverlay />;
  }
  return <div className="flex flex-col h-screen overflow-hidden">
      <TitleBar />
      <GlobalLoadingOverlay />
      <GlobalBuildErrorOverlay />
      <main className="flex-1 overflow-hidden">
        {children}
      </main>
      <SdkUpdateWaitingDialog isOpen={showWaitingDialog} currentVersion={currentSdkVersion} requiredVersion={MINIMUM_SDK_VERSION} currentEngineVersion={currentSdkEngineVersion} requiredEngineVersion={ENGINE_VERSION} reason={sdkCompatibilityReason} />
      <VersionCheckDialog isOpen={!IS_DEV && (showManualDialog || isInitialized && !isVersionValid && !showWaitingDialog && !!currentSdkVersion)} currentVersion={currentSdkVersion} requiredVersion={MINIMUM_SDK_VERSION} currentEngineVersion={currentSdkEngineVersion} requiredEngineVersion={ENGINE_VERSION} reason={sdkCompatibilityReason} onRetry={() => {
      void requestManualUpdateCheck();
    }} />
      <Toaster />
    </div>;
});
ClientAppShell.displayName = 'ClientAppShell';
export const ClientLayout = t0 => {
  const $ = _c(7);
  const {
    children,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "app" : t1;
  if (variant === "auth") {
    let t2;
    if ($[0] !== children) {
      t2 = <main className="h-full min-h-full">{children}</main>;
      $[0] = children;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Toaster />;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== t2) {
      t4 = <ClientLayoutProviders>{t2}{t3}</ClientLayoutProviders>;
      $[3] = t2;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  let t2;
  if ($[5] !== children) {
    t2 = <ClientLayoutProviders><ClientAppShell>{children}</ClientAppShell></ClientLayoutProviders>;
    $[5] = children;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
ClientLayout.displayName = 'ClientLayout';